import { Graphics } from 'pixi.js';

import { ClientRegistry } from '../components';
import {
  ClientFilteredComponentSystem,
  FilteredClientComponentSystemUpdateEntityArgs,
} from './ClientBase';

const DESTINATION_SIZE = 8; // Size of destination square

/**
 * Renders debug visualization for entity destinations.
 * Shows a square at the destination location, colored per-entity.
 * Toggled via the Debug Panel.
 */
export class DebugDestinationRenderSystem extends ClientFilteredComponentSystem {
  public componentClasses: (keyof ClientRegistry)[] = ['Destination', 'DebugColor', 'Transform'];

  // Store graphics per entity to avoid shared state conflicts
  private destinationGraphics: Map<number, Graphics> = new Map();

  updateEntity({ world, entityId }: FilteredClientComponentSystemUpdateEntityArgs): void {
    // Check debug toggle
    const existingGraphics = this.destinationGraphics.get(entityId);

    if (!this.clientServices.debug.state.showDestinationMarkers) {
      // Hide existing graphics if toggle is off
      if (existingGraphics) {
        existingGraphics.visible = false;
      }
      return;
    }

    const destination = world.getComponent(entityId, 'Destination')!;
    const debugColor = world.getComponent(entityId, 'DebugColor')!;
    const transform = world.getComponent(entityId, 'Transform')!;

    // Get local player's scene to filter rendering
    const localEntityId = this.clientServices.localPlayer.entityId;
    const localPlayerTransform = localEntityId
      ? world.getComponent(localEntityId, 'Transform')
      : null;
    const currentSceneId = localPlayerTransform?.sceneId;

    // Only render for entities in the same scene as player
    if (!currentSceneId || transform.sceneId !== currentSceneId) {
      return;
    }

    let graphics = existingGraphics;
    if (!graphics) {
      graphics = new Graphics();
      graphics.zIndex = 10000;
      this.clientServices.render.stage?.addChild(graphics);
      this.destinationGraphics.set(entityId, graphics);
    }

    graphics.visible = true;
    graphics.clear();

    // Draw destination square
    graphics.rect(
      destination.x - DESTINATION_SIZE / 2,
      destination.y - DESTINATION_SIZE / 2,
      DESTINATION_SIZE,
      DESTINATION_SIZE,
    );
    graphics.fill({ color: debugColor.color, alpha: 0.7 });
    graphics.stroke({ color: debugColor.color, width: 2, alpha: 1 });
  }

  /**
   * Cleanup graphics for entities that no longer exist or lost Destination component.
   */
  override run(deltaTime?: number): void {
    const world = this.getWorld();
    // Cleanup stale graphics before processing
    for (const [entityId, graphics] of this.destinationGraphics) {
      const hasDestination = world.getComponent(entityId, 'Destination');
      if (!hasDestination) {
        graphics.destroy();
        this.destinationGraphics.delete(entityId);
      }
    }

    super.run(deltaTime);
  }
}
